import { useEffect } from 'react';

/**
 * Modal de alerta reutilizable para LMS
 * @param {boolean} open - Si el modal está abierto
 * @param {function} onClose - Callback al cerrar
 * @param {string|JSX.Element} title - Título del modal
 * @param {string|JSX.Element} message - Mensaje del modal (puede ser HTML/JSX)
 * @param {string} buttonText - Texto del botón (default: "Aceptar")
 * @param {string} variant - Variante visual: 'info', 'success', 'warning', 'error'
 */
export default function AlertDialog({
    open,
    onClose,
    onCancel, // Nuevo: soporte para cancelar
    title = 'Información',
    message = '',
    buttonText = 'Aceptar',
    cancelText = 'Cancelar', // Nuevo
    variant,
    type // Soportar 'type' como alias de variant
}) {
    // Si se pasa type pero no variant, usar type
    const finalVariant = variant || type || 'info';
    // Cerrar con ESC
    useEffect(() => {
        if (!open) return;
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [open, onClose]);

    if (!open) return null;

    // Estilos e iconos según variante
    const variantConfig = {
        info: {
            icon: 'i',
            iconBg: 'var(--color-primary)',
        },
        success: {
            icon: '✓',
            iconBg: '#10b981',
        },
        warning: {
            icon: '⚠',
            iconBg: '#f59e0b',
        },
        error: {
            icon: '✕',
            iconBg: '#dc2626',
        }
    };

    const config = variantConfig[finalVariant] || variantConfig.info;

    return (
        <div
            className="roles-confirm-overlay"
            role="dialog"
            aria-modal="true"
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15,23,42,.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                zIndex: 1000,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="roles-confirm-card"
                style={{
                    width: 'min(450px, 96vw)',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 10px 30px rgba(0,0,0,.25)',
                    padding: '1.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                }}
            >
                {/* Icono y Título */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                }}>
                    <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: config.iconBg,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        flexShrink: 0
                    }}>
                        {config.icon}
                    </div>
                    <div style={{
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        color: '#1e293b',
                        lineHeight: 1.3
                    }}>
                        {title}
                    </div>
                </div>

                {/* Mensaje */}
                <div style={{
                    fontSize: '0.95rem',
                    color: '#475569',
                    lineHeight: 1.5,
                    paddingLeft: '3.25rem'
                }}>
                    {message}
                </div>

                {/* Botones */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.75rem',
                    marginTop: '0.5rem'
                }}>
                    {onCancel && (
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={onCancel}
                        >
                            {cancelText}
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn-action"
                        onClick={onClose}
                    >
                        {buttonText}
                    </button>
                </div>
            </div>
        </div>
    );
}
